// MailBIT Payroll — Shared TypeScript Types

export interface Employee {
  id: string;
  employee_code: string;
  title_th: string;
  first_name_th: string;
  last_name_th: string;
  first_name_en?: string;
  last_name_en?: string;
  nickname?: string;
  national_id: string;
  email: string;
  department: string;
  position?: string;
  bank_name?: string;
  bank_account?: string;
  employment_type: 'monthly' | 'daily';
  start_date: string;
  end_date?: string;
  base_salary: number;
  ss_exempt: boolean;
  ss_exempt_reason?: string;
  company_tax_id: string;
  payment_method: 'bank_transfer' | 'check';
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface EmployeeFormData {
  employee_code: string;
  title_th: string;
  first_name_th: string;
  last_name_th: string;
  first_name_en?: string;
  last_name_en?: string;
  nickname?: string;
  national_id: string;
  email: string;
  department: string;
  position?: string;
  bank_name?: string;
  bank_account?: string;
  employment_type: 'monthly' | 'daily';
  start_date: string;
  end_date?: string | null;
  base_salary: number;
  ss_exempt: boolean;
  ss_exempt_reason?: string;
  company_tax_id: string;
  payment_method: 'bank_transfer' | 'check';
  is_active: boolean;
}

export interface SocialSecurityRate {
  id: string;
  rate_type: 'employee' | 'employer';
  rate_percent: number;
  max_salary_base: number;
  max_contribution?: number;
  effective_from: string;
  effective_to?: string;
  description?: string;
  created_at: string;
}

export interface TaxBracket {
  id: string;
  bracket_order: number;
  min_income: number;
  max_income?: number;
  rate_percent: number;
  year: number;
  is_active: boolean;
  created_at: string;
}

export interface TaxAllowance {
  id: string;
  allowance_type: string;
  amount: number;
  year: number;
  description?: string;
  is_active: boolean;
  created_at: string;
}

export interface PayrollPeriod {
  id: string;
  year: number;
  month: number;
  period_start: string;
  period_end: string;
  pay_date: string;
  status: 'draft' | 'finalized' | 'paid' | 'emailed';
  created_at: string;
  finalized_at?: string;
}

export interface PayrollEntry {
  id: string;
  period_id: string;
  employee_id: string;

  // Income
  base_salary: number;
  overtime: number;
  food_allowance: number;
  diligence_bonus: number;
  special_pay: number;
  bonus: number;
  commission: number;
  other_income: number;
  other_income_note?: string;

  // Deductions
  absent_deduction: number;
  leave_deduction: number;
  late_deduction: number;
  forgot_scan: number;
  income_tax: number;
  social_security: number;
  social_security_employer: number;
  other_deduction: number;
  other_deduction_note?: string;

  // Totals
  total_income: number;
  total_deduction: number;
  net_pay: number;

  // YTD
  ytd_income: number;
  ytd_tax: number;
  ytd_social_security: number;

  notes?: string;
  created_at: string;
  updated_at: string;

  // Joined fields (not in DB)
  employee?: Employee;
}

export interface PayrollLog {
  id: string;
  period_id?: string;
  employee_id?: string;
  action: string;
  details?: string;
  performed_by: string;
  created_at: string;
}

// API response types
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  total: number;
  page: number;
  limit: number;
}

// Auth types
export interface LoginRequest {
  email: string;
  totp_code: string;
}

export interface AuthSession {
  email: string;
  expires_at: string;
}

export interface TOTPSetupResponse {
  qr_code: string;
  secret: string;
  email: string;
}

// Report types
export interface QuickBooksJournalEntry {
  account: string;
  debit: number;
  credit: number;
}

export interface PND1Entry {
  employee_name: string;
  national_id: string;
  income: number;
  tax_withheld: number;
}

export interface WHTCertificate {
  employee: Employee;
  year: number;
  total_income: number;
  total_tax: number;
  months: { month: number; income: number; tax: number }[];
}

// Dashboard stats
export interface DashboardStats {
  active_employees: number;
  current_period_status: string;
  total_payroll: number;
  total_tax: number;
  total_ss: number;
  total_net: number;
}

// Import types
export interface ImportPreview {
  rows: Record<string, unknown>[];
  columns: string[];
  mapping: Record<string, string>;
  errors: string[];
  warnings: string[];
}

// Thai title options
export const THAI_TITLES = ['นาย', 'นาง', 'นางสาว'] as const;

// Company tax IDs
export const COMPANY_TAX_IDS = [
  { id: '3032261302', label: 'MailBIT (3032261302)' },
  { id: '3034032931', label: 'MailBIT (3034032931)' },
  { id: '0105549080337', label: 'MailBIT (0105549080337)' },
] as const;

// Department options
export const DEPARTMENTS = [
  'CS',
  'Management',
  'Digital Marketing',
  'Sales',
  'Admin',
  'Accounting',
] as const;

// Month names (Thai/English)
export const MONTHS_TH = [
  'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน',
  'พฤษภาคม', 'มิถุนายน', 'กรกฎาคม', 'สิงหาคม',
  'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
] as const;

export const MONTHS_EN = [
  'January', 'February', 'March', 'April',
  'May', 'June', 'July', 'August',
  'September', 'October', 'November', 'December'
] as const;
